import React, { useEffect, useId, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import stepsArt from '../../assets/illustrations/landing-renewal/progress-painted.webp'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/Primitives'
import { useAuth } from '../../contexts/authContext'
import { useI18n } from '../../i18n/I18nContext'
import { translateTemplate } from '../../services/translations'
import { openInlineCheckout } from '../../lib/paystackCheckout'
import { setPendingPlan, clearPendingPlan, type PlanId } from '../../lib/pendingPlan'
import { MONTHLY_PRICE_LABEL, YEARLY_PRICE_LABEL, YEARLY_MONTHLY_EQUIV_LABEL } from '../../lib/pricing'
import { classifyError, type ErrorKind } from '../../lib/friendlyError'
import './PaywallPanel.css'

export type PaywallReason = 'locked' | 'upgrade' | 'expired' | 'renew'
interface PaywallPanelProps {
    mode: 'sheet' | 'page'
    classId?: string
    stageName?: string
    reason?: PaywallReason
    onClose?: () => void
    headingId?: string
    /** Let a containing sheet yield keyboard focus to Paystack's own popup. */
    onProcessingChange?: (processing: boolean) => void
}
type Cycle = 'monthly' | 'yearly'
const FEATURES = ['studyMat', 'certificate', 'webMobile'] as const
const focusClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dhtn-text)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--dhtn-canvas)]'

const PaywallPanel: React.FC<PaywallPanelProps> = ({ mode, classId, stageName, reason = 'upgrade', onClose, headingId, onProcessingChange }) => {
    const navigate = useNavigate()
    const { user, isLoading } = useAuth()
    const { t } = useI18n()
    const [cycle, setCycle] = useState<Cycle>('yearly')
    const [processing, setProcessing] = useState(false)
    const [payError, setPayError] = useState<ErrorKind | 'validation' | null>(null)
    const processingRef = useRef(false)
    const actionRef = useRef<HTMLButtonElement>(null)
    const returnFocusRef = useRef(false)
    const checkoutRef = useRef<AbortController | null>(null)
    const summaryId = useId()
    const yearly = cycle === 'yearly'
    const price = yearly ? YEARLY_PRICE_LABEL : MONTHLY_PRICE_LABEL

    useEffect(() => { onProcessingChange?.(processing) }, [processing, onProcessingChange])
    useEffect(() => () => { checkoutRef.current?.abort() }, [])
    useEffect(() => {
        if (!processing && returnFocusRef.current) {
            returnFocusRef.current = false
            actionRef.current?.focus()
        }
    }, [processing])

    const headline = reason === 'locked' && stageName
        ? translateTemplate(t('paywall.headlineLocked'), { stageName })
        : t(reason === 'expired' ? 'paywall.headlineExpired' : reason === 'renew' ? 'paywall.headlineRenew' : 'paywall.headlineDefault')
    const lead = t(reason === 'expired' ? 'paywall.leadExpired' : reason === 'renew' ? 'paywall.leadRenew' : 'paywall.lead')

    const pay = async () => {
        if (processingRef.current || isLoading) return
        const plan: PlanId = yearly ? 'FULL_ACCESS_YEARLY' : 'FULL_ACCESS_MONTHLY'
        setPendingPlan(plan)
        if (!user) {
            navigate('/signup', { state: { returnTo: '/checkout' } })
            return
        }
        if (classId) sessionStorage.setItem('paymentReturnClassId', classId)
        else sessionStorage.removeItem('paymentReturnClassId')
        setPayError(null)
        processingRef.current = true
        setProcessing(true)
        const controller = new AbortController()
        checkoutRef.current = controller
        const finish = (failure: ErrorKind | 'validation' | null) => {
            if (controller.signal.aborted || checkoutRef.current !== controller) return
            processingRef.current = false
            returnFocusRef.current = true
            setProcessing(false)
            setPayError(failure)
        }
        const failed = (error: unknown) => {
            const status = error instanceof Error ? (error as Error & { status?: number }).status : undefined
            finish(status === 400 || status === 422 ? 'validation' : classifyError(error))
        }
        try {
            await openInlineCheckout(plan, {
                onSuccess: reference => {
                    clearPendingPlan()
                    onClose?.()
                    navigate(`/payment/callback?reference=${encodeURIComponent(reference)}`)
                },
                onCancel: () => finish(null),
                onError: failed,
            }, controller.signal)
        } catch (error) {
            failed(error)
        }
    }

    const exit = () => {
        checkoutRef.current?.abort()
        if (mode === 'sheet') onClose?.()
        else if (window.history.length > 1) navigate(-1)
        else navigate('/')
    }
    const errorText = payError && t(payError === 'offline' ? 'errorPage.offline.body' : payError === 'server' ? 'errorPage.serverError.body' : payError === 'auth' ? 'errorPage.sessionExpired.body' : payError === 'forbidden' ? 'errorPage.forbidden.body' : payError === 'validation' ? 'paywall.paymentValidationError' : 'paywall.paymentError')

    // The /pricing page (founder, 8 Oct: the two-column form card read as generic): a centred story.
    // Painting and promise, the two plans side by side, one action, then what is included and what is free.
    if (mode === 'page') return (
        <div className="pricing">
            <header className="pricing-head">
                <img src={stepsArt} alt="" width={240} height={185} />
                <h1 id={headingId} className="font-heading"><bdi>{headline}</bdi></h1>
                <p>{lead}</p>
            </header>
            <div className="pricing-plans" role="radiogroup" aria-label={t('pricingPage.billingLabel')}>
                {(['monthly', 'yearly'] as Cycle[]).map(option => (
                    <button key={option} type="button" role="radio" aria-checked={cycle === option} data-plan={option} disabled={processing}
                        onClick={() => { setCycle(option); setPayError(null) }} className={`pricing-plan ${focusClass}`}>
                        <span className="pricing-plan-top">
                            <span className="pricing-plan-name">{t(`pricingPage.toggle.${option}`)}</span>
                            {option === 'yearly' && <span className="pricing-save">{t('pricingPage.toggle.saveBadge')}</span>}
                        </span>
                        <span className="pricing-price">
                            <bdi dir="ltr">{option === 'yearly' ? YEARLY_PRICE_LABEL : MONTHLY_PRICE_LABEL}</bdi>
                            <span>{t(option === 'yearly' ? 'pricingPage.period.year' : 'pricingPage.period.month')}</span>
                        </span>
                        <span className="pricing-plan-note">
                            {option === 'yearly'
                                ? translateTemplate(t('paywall.perMonthBilledYearly'), { price: `\u2068${YEARLY_MONTHLY_EQUIV_LABEL}\u2069` })
                                : t('paywall.promise')}
                        </span>
                    </button>
                ))}
            </div>
            <div className="pricing-action">
                <p id={summaryId} className="sr-only" aria-live="polite">{price} {t(yearly ? 'pricingPage.period.year' : 'pricingPage.period.month')}</p>
                <Button ref={actionRef} variant="primary" fullWidth onClick={pay} isLoading={processing} disabled={processing || isLoading} aria-describedby={summaryId} className="!min-w-0">
                    {t(user ? 'pricingPage.fullAccess.cta' : 'signup.submitButton')}
                </Button>
                {errorText && <p role="alert" className="pricing-error">{errorText}</p>}
                <p className="pricing-small">{t(user ? 'pricingPage.footerNote' : 'paywall.signupFirst')}</p>
            </div>
            <div className="pricing-more">
                <section aria-labelledby="pricing-included">
                    <h2 id="pricing-included">{t('pricingPage.includedTitle')}</h2>
                    <ul aria-label={t('pricingPage.featuresLabel')}>
                        {FEATURES.map(key => (
                            <li key={key}><Check size={19} strokeWidth={2.75} aria-hidden="true" />{t(`pricingPage.feature.${key}`)}</li>
                        ))}
                    </ul>
                </section>
                <section aria-labelledby="pricing-free">
                    <h2 id="pricing-free">{t('pricingPage.freeTitle')}</h2>
                    <p>{t('pricingPage.freeBody')}</p>
                </section>
            </div>
        </div>
    )

    return (
        <div className="space-y-6">
            <div className="min-w-0">
                <h2 id={headingId} className="break-words pe-10 font-heading text-[1.65rem] font-semibold leading-[1.35] tracking-tight text-[var(--dhtn-text)] sm:text-[1.85rem]">
                    <bdi>{headline}</bdi>
                </h2>
                <p className="mt-4 max-w-md text-base font-medium leading-7 text-[var(--dhtn-text-muted)]">{lead}</p>
                <ul className="mt-6 space-y-3" aria-label={t('pricingPage.featuresLabel')}>
                    {FEATURES.map(key => (
                        <li key={key} className="flex items-start gap-3">
                            <Check className="mt-0.5 shrink-0" size={19} strokeWidth={2.75} aria-hidden="true" />
                            <span className="text-base font-semibold leading-6">{t(`pricingPage.feature.${key}`)}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="min-w-0 border-t-2 border-[var(--dhtn-border)] pt-5">
                <div className="grid grid-cols-2 gap-1 rounded-control border-2 border-[var(--dhtn-border)] bg-[var(--dhtn-surface-raised)] p-1" role="group" aria-label={t('pricingPage.billingLabel')}>
                    {(['monthly', 'yearly'] as Cycle[]).map(option => (
                        <button key={option} type="button" aria-pressed={cycle === option} disabled={processing}
                            onClick={() => { setCycle(option); setPayError(null) }}
                            className={`min-h-[44px] min-w-0 rounded-[calc(var(--dhtn-radius-control)-4px)] px-3 py-2 text-sm font-semibold leading-tight disabled:cursor-wait ${focusClass} ${cycle === option ? 'bg-[var(--dhtn-canvas)] text-[var(--dhtn-text)] shadow-[0_0_0_2px_var(--dhtn-control-edge)]' : 'text-[var(--dhtn-text-muted)] hover:text-[var(--dhtn-text)]'}`}>
                            {t(`pricingPage.toggle.${option}`)}
                        </button>
                    ))}
                </div>
                <div id={summaryId} className="mt-6" aria-live="polite" aria-atomic="true">
                    <p className="flex flex-wrap items-baseline gap-x-2.5 gap-y-2">
                        <bdi dir="ltr" className="font-heading text-[2.35rem] font-semibold leading-none tracking-tight text-[var(--dhtn-text)] sm:text-[2.75rem]">{price}</bdi>
                        <span className="text-base font-semibold text-[var(--dhtn-text-muted)]">{t(yearly ? 'pricingPage.period.year' : 'pricingPage.period.month')}</span>
                    </p>
                    {yearly && <p className="mt-3 text-sm font-medium leading-6 text-[var(--dhtn-text-muted)]">
                        {translateTemplate(t('paywall.perMonthBilledYearly'), { price: `\u2068${YEARLY_MONTHLY_EQUIV_LABEL}\u2069` })}
                        <span className="mt-2 block font-semibold text-[var(--dhtn-text)]">{t('paywall.yearlySaving')}</span>
                    </p>}
                </div>
                <p className="mt-5 text-sm font-medium leading-6 text-[var(--dhtn-text-muted)]">{t('paywall.promise')}</p>
                <Button ref={actionRef} variant="primary" fullWidth onClick={pay} isLoading={processing} disabled={processing || isLoading} aria-describedby={summaryId} className="mt-6 !min-w-0">
                    {t(user ? 'pricingPage.fullAccess.cta' : 'signup.submitButton')}
                </Button>
                {payError && <p role="alert" className="mt-4 border-s-2 border-error-500 ps-3 text-sm font-semibold leading-6 text-[var(--dhtn-text)]">{errorText}</p>}
                <p className="mt-4 text-sm font-medium leading-6 text-[var(--dhtn-text-muted)]">{t(user ? 'paywall.secure' : 'paywall.signupFirst')}</p>
                <button type="button" onClick={exit}
                    className={`mt-4 min-h-[44px] w-full rounded-control text-sm font-semibold text-[var(--dhtn-text-muted)] hover:text-[var(--dhtn-text)] disabled:cursor-wait ${focusClass}`}>
                    {t('paywall.goBack')}
                </button>
            </div>
        </div>
    )
}
export default PaywallPanel
